import { useState } from 'react';
import { Star, Flame, Leaf, Wheat, Filter, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

const dietaryFilters = [
  { key: 'is_vegetarian', label: 'Vegetarian', icon: Leaf, color: 'text-green-500', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  { key: 'is_vegan', label: 'Vegan', icon: Leaf, color: 'text-emerald-600', bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' },
  { key: 'is_gluten_free', label: 'Gluten-Free', icon: Wheat, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
  { key: 'is_spicy', label: 'Spicy', icon: Flame, color: 'text-red-500', bgColor: 'bg-red-100 dark:bg-red-900/30' },
];

export default function MenuSection({ 
  menu, 
  activeCategory, 
  setActiveCategory, 
  onItemClick 
}) {
  const [activeFilters, setActiveFilters] = useState([]);

  const toggleFilter = (filterKey) => {
    setActiveFilters(prev => 
      prev.includes(filterKey) 
        ? prev.filter(f => f !== filterKey)
        : [...prev, filterKey]
    );
  };

  const clearFilters = () => {
    setActiveFilters([]);
  };

  // Filter menu items based on active dietary filters
  const filterItems = (items) => {
    if (activeFilters.length === 0) return items;
    return items.filter(item => 
      activeFilters.every(filter => item[filter])
    );
  };

  // Count total filtered items
  const getFilteredCount = () => {
    let total = 0;
    menu.forEach(category => {
      total += filterItems(category.items).length;
    });
    return total;
  };

  return (
    <>
      {/* Category Navigation + Dietary Filters */}
      <div className="space-y-3 mb-6">
        {/* Category Pills */}
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-2">
            {menu.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.name)}
                className={`cuisine-pill ${activeCategory === category.name ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}
                data-testid={`category-${category.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {category.name}
                {activeFilters.length > 0 && (
                  <span className="ml-1 text-xs opacity-70">
                    ({filterItems(category.items).length})
                  </span>
                )}
              </button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Dietary Filter Bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                Dietary Filters
                {activeFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full">
                    {activeFilters.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="start">
              <div className="space-y-3">
                <div className="font-medium text-sm">Filter by dietary preference</div>
                {dietaryFilters.map((filter) => {
                  const Icon = filter.icon;
                  return (
                    <div key={filter.key} className="flex items-center space-x-2">
                      <Checkbox 
                        id={filter.key}
                        checked={activeFilters.includes(filter.key)}
                        onCheckedChange={() => toggleFilter(filter.key)}
                      />
                      <Label 
                        htmlFor={filter.key} 
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <Icon className={`h-4 w-4 ${filter.color}`} />
                        {filter.label}
                      </Label>
                    </div>
                  );
                })}
                {activeFilters.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="w-full mt-2"
                  >
                    Clear all filters
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Active Filter Pills */}
          {activeFilters.map(filterKey => {
            const filter = dietaryFilters.find(f => f.key === filterKey);
            if (!filter) return null;
            const Icon = filter.icon;
            return (
              <Badge 
                key={filterKey}
                variant="secondary"
                className={`gap-1 cursor-pointer ${filter.bgColor}`}
                onClick={() => toggleFilter(filterKey)}
              >
                <Icon className={`h-3 w-3 ${filter.color}`} />
                {filter.label}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            );
          })}

          {activeFilters.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {getFilteredCount()} items found
            </span>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <div className="space-y-8">
        {menu.map((category) => {
          const filteredItems = filterItems(category.items);
          
          return (
            <div 
              key={category.id} 
              id={`category-${category.id}`}
              className={activeCategory === category.name ? '' : 'hidden'}
            >
              <h2 className="text-xl font-bold mb-4">{category.name}</h2>
              
              {filteredItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Leaf className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No items match your dietary filters in this category</p>
                  <Button 
                    variant="link" 
                    onClick={clearFilters}
                    className="mt-2"
                  >
                    Clear filters to see all items
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredItems.map((item) => (
                    <Card 
                      key={item.id}
                      className="overflow-hidden cursor-pointer card-hover"
                      onClick={() => onItemClick(item)}
                      data-testid={`menu-item-${item.id}`}
                    >
                      <div className="flex">
                        <div className="flex-1 p-4">
                          <div className="flex items-start gap-2 mb-1">
                            <h3 className="font-semibold">{item.name}</h3>
                            <div className="flex gap-1 flex-wrap">
                              {item.is_popular && (
                                <Badge variant="outline" className="badge-popular text-xs">Popular</Badge>
                              )}
                              {item.is_spicy && (
                                <Flame className="h-4 w-4 text-red-500" title="Spicy" />
                              )}
                              {item.is_vegetarian && (
                                <Leaf className="h-4 w-4 text-green-500" title="Vegetarian" />
                              )}
                              {item.is_vegan && (
                                <Badge variant="outline" className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-300">
                                  Vegan
                                </Badge>
                              )}
                              {item.is_gluten_free && (
                                <Badge variant="outline" className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-300">
                                  GF
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {item.description}
                          </p>
                          {item.calories && (
                            <p className="text-xs text-muted-foreground mb-1">
                              {item.calories} cal
                            </p>
                          )}
                          <p className="font-semibold text-primary price-tag">
                            ${item.price.toFixed(2)}
                          </p>
                        </div>
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-28 h-28 object-cover"
                          />
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
